/**
 * DocsStage — the "Tender documents" facet: a document list on the left, a clean inline
 * preview on the right (title bar + open-full + download). Auto-selects the first available
 * document (original → working → notice) so the preview is never needlessly empty.
 */
import React, { useEffect, useState } from 'react';
import { FileText, File as FileIcon, Upload, Loader2, Maximize2, Download } from 'lucide-react';
import { forgeAPI } from '../../../services/api';
import { useForge } from '../ForgeContext';
import PdfModal from '../PdfModal';

const DocLink = ({ active, onClick, name }) => (
  <button className={`forge-doc-item ${active ? 'forge-doc-active' : ''}`} onClick={onClick}
    style={{ width: '100%', textAlign: 'left', border: 'none', background: 'none', cursor: 'pointer' }}>
    <FileIcon size={13} />
    <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</span>
  </button>
);

const DocsStage = () => {
  const { tenderId } = useForge();
  const [docs, setDocs] = useState(null);
  const [sel, setSel] = useState(null);
  const [busy, setBusy] = useState(false);
  const [full, setFull] = useState(null);

  const workingUrl = (d) => `/api/forge/documents/${encodeURIComponent(tenderId)}/working/${encodeURIComponent(d.doc_id)}/download`;

  const load = () => forgeAPI.getDocuments(tenderId).then((d) => {
    setDocs(d);
    setSel((prev) => prev || (
      d?.original?.[0] ? { url: d.original[0].view_path, name: d.original[0].filename }
        : d?.working?.[0] ? { url: workingUrl(d.working[0]), name: d.working[0].filename }
          : d?.notice_url ? { url: d.notice_url, name: 'Tender notice' } : null));
  }).catch(() => {});

  useEffect(() => { setSel(null); load(); /* eslint-disable-next-line */ }, [tenderId]);

  const originals = docs?.original || [];
  const working = docs?.working || [];

  const onUpload = async (e) => {
    const file = e.target.files && e.target.files[0]; e.target.value = '';
    if (!file) return;
    setBusy(true);
    try { await forgeAPI.uploadWorkingDoc(tenderId, file); await load(); } finally { setBusy(false); }
  };

  return (
    <div className="forge-cc-pane forge-ck-docs">
      <div className="forge-ck-docs-list forge-cc-scroll">
        <div className="forge-ck-sec-title"><FileText size={13} /> Documents</div>
        {docs?.notice_url && <DocLink active={sel?.url === docs.notice_url} onClick={() => setSel({ url: docs.notice_url, name: 'Tender notice' })} name="Tender notice" />}
        {originals.map((d) => <DocLink key={d.document_id} active={sel?.url === d.view_path} onClick={() => setSel({ url: d.view_path, name: d.filename })} name={d.filename} />)}
        {working.length > 0 && <div className="forge-doc-title" style={{ marginTop: 10 }}>Working documents</div>}
        {working.map((d) => <DocLink key={d.doc_id} active={sel?.url === workingUrl(d)} onClick={() => setSel({ url: workingUrl(d), name: d.filename })} name={d.filename} />)}
        <label className="forge-btn-sm" style={{ cursor: 'pointer', marginTop: 12, display: 'inline-flex', gap: 6, alignItems: 'center' }}>
          {busy ? <Loader2 size={13} className="forge-spin" /> : <Upload size={13} />} Upload
          <input type="file" style={{ display: 'none' }} onChange={onUpload} />
        </label>
      </div>
      <div className="forge-ck-docs-view">
        {sel ? (
          <>
            <div className="forge-ck-docs-bar">
              <span className="forge-ck-docs-name" title={sel.name}>{sel.name}</span>
              <span style={{ display: 'flex', gap: 6 }}>
                <button className="forge-btn-ghost" onClick={() => setFull(sel)} title="Open full screen"><Maximize2 size={13} /></button>
                <a className="forge-btn-ghost" href={sel.url} download title="Download"><Download size={13} /></a>
              </span>
            </div>
            <iframe title={sel.name} src={sel.url} className="forge-ck-docs-frame" />
          </>
        ) : (
          <div className="forge-ck-docs-empty">
            <FileText size={26} />
            <div>No documents yet.</div>
            <div className="forge-muted" style={{ fontSize: 12 }}>Upload one, or they arrive from Discovery / Accio.</div>
          </div>
        )}
      </div>
      {full && <PdfModal url={full.url} title={full.name} onClose={() => setFull(null)} />}
    </div>
  );
};

export default DocsStage;
